import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { PageBackButton } from '@/components/common/PageBackButton'
import { MyGrowerPickerList } from '@/components/growers/my-grower-picker-list'
import { resolveMyGrowerEntry } from '@/components/growers/resolve-my-grower-entry'
import { Button } from '@/components/ui/button'
import { useMyGrowers } from '@/hooks/use-care-logs-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/my-grower/',
)({
    component: MyGrowerEntryPage,
})

/**
 * Entry point for a household reading its own profile. Most accounts are linked
 * to exactly one household and go straight through; accounts linked to several
 * pick one first.
 */
function MyGrowerEntryPage() {
    const navigate = useNavigate()
    const { data: growers, isLoading, isError, refetch } = useMyGrowers(true)
    const entry = resolveMyGrowerEntry({
        loading: isLoading,
        failed: isError,
        growers,
    })

    const redirectTo = entry.kind === 'redirect' ? entry.growerId : null
    useEffect(() => {
        if (!redirectTo) return
        void navigate({
            to: '/agriculture/my-grower/$growerId',
            params: { growerId: redirectTo },
            replace: true,
        })
    }, [navigate, redirectTo])

    if (entry.kind === 'loading' || entry.kind === 'redirect') {
        return (
            <div className="p-4 text-sm text-muted-foreground md:p-6">
                {m.my_grower_loading()}
            </div>
        )
    }

    if (entry.kind === 'error') {
        return (
            <div className="space-y-3 page-pad">
                <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                    {m.my_grower_load_error()}
                </p>
                <Button type="button" onClick={() => void refetch()}>
                    {m.my_grower_retry()}
                </Button>
            </div>
        )
    }

    if (entry.kind === 'empty') {
        return (
            <div className="page-pad">
                <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                    {m.my_grower_empty()}
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <header className="space-y-1">
                    <h1 className="text-xl font-semibold md:text-2xl">
                        {m.my_grower_pick_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.my_grower_pick_subtitle()}
                    </p>
                </header>
            </div>

            <MyGrowerPickerList
                growers={entry.growers}
                onPick={(g) =>
                    void navigate({
                        to: '/agriculture/my-grower/$growerId',
                        params: { growerId: g.id },
                    })
                }
            />
        </div>
    )
}
